/**
 * Step 1 — AI Type.
 *
 * Five mutually exclusive categories with the source's descriptions verbatim, plus a
 * detail panel with the three worked examples for whichever is selected.
 *
 * The source rendered these as clickable <div role="button"> elements, which gave no
 * group semantics, no selected state to a screen reader and no arrow-key navigation.
 * They are real radio inputs here, wrapped in a fieldset/legend.
 */

import React from 'react';
import {
  AI_TYPES,
  AI_TYPE_DESCRIPTIONS,
  AI_TYPE_ICONS,
  AI_TYPE_LABELS,
  AI_TYPE_TONE,
} from '../../../constants/aiGovernance';
import type { StepProps } from './types';

const Step1AiType: React.FC<StepProps> = ({ useCase, update }) => {
  const selected = useCase.aiType;

  return (
    <>
      <fieldset className="np-fieldset">
        <legend className="np-fieldset-legend">What type of AI are you onboarding?</legend>
        <p className="np-sub mb-3">
          Choose the category that best fits. Not sure? Pick the closest one — you can update it later.
        </p>

        <div className="np-grid np-grid-3">
          {AI_TYPES.map(type => {
            const tone = AI_TYPE_TONE[type];
            const isSelected = selected === type;

            return (
              <label key={type} className={`np-choice ${isSelected ? `selected np-tone-${tone}` : ''}`}>
                <input
                  type="radio"
                  name="ai-use-case-type"
                  className="visually-hidden"
                  value={type}
                  checked={isSelected}
                  onChange={() => update({ aiType: type })}
                />
                <span className={`np-icon-badge np-tone-${tone}`}>
                  <i className={AI_TYPE_ICONS[type]}></i>
                </span>
                <span className="np-choice-body">
                  <span className="np-choice-label d-flex align-items-center gap-2">
                    {AI_TYPE_LABELS[type]}
                    {isSelected && <i className="ri-checkbox-circle-fill ms-auto text-success"></i>}
                  </span>
                  <span className="np-choice-desc d-block">{AI_TYPE_DESCRIPTIONS[type].short}</span>
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      {selected ? (
        <div className={`np-card np-card-accent-${AI_TYPE_TONE[selected]}`}>
          <div className="np-card-body">
            <div className="d-flex align-items-start gap-3">
              <span className={`np-icon-badge np-tone-${AI_TYPE_TONE[selected]}`}>
                <i className={AI_TYPE_ICONS[selected]}></i>
              </span>
              <div className="flex-grow-1">
                <div className="fw-semibold mb-1">{AI_TYPE_LABELS[selected]}</div>
                <p className="np-sub mb-3" style={{ lineHeight: 1.6 }}>
                  {AI_TYPE_DESCRIPTIONS[selected].detail}
                </p>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span className="np-hint m-0">Typical examples</span>
                  {AI_TYPE_DESCRIPTIONS[selected].examples.map(example => (
                    <span key={example} className={`np-chip np-tone-${AI_TYPE_TONE[selected]}`}>{example}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="np-banner np-tone-neutral">
          <i className="ri-arrow-up-line"></i>
          <span>Select a type above to see more detail and examples</span>
        </div>
      )}
    </>
  );
};

export default Step1AiType;
